import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from '../../i18n/LanguageContext';
import { IconX, IconCheck, IconUpload } from '@tabler/icons-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = 256;
  canvas.height = 256;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    256,
    256
  );

  return new Promise((resolve) => {
    canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
  });
}

export default function PhotoCropModal({ onSave, onClose }) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => setImageSrc(reader.result));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      await onSave(blob);
      onClose();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 200,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 'var(--radius-xl)',
        width: 480,
        overflow: 'hidden',
        border: '0.5px solid var(--color-border-light)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '0.5px solid var(--color-border-light)',
        }}>
          <div style={{ fontSize: 16, fontWeight: 400 }}>{t('photo.title')}</div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}>
            <IconX size={18} stroke={1.5} />
          </div>
        </div>

        {/* Content */}
        {!imageSrc ? (
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'var(--color-bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <IconUpload size={28} color="var(--color-text-tertiary)" stroke={1.5} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 6 }}>{t('photo.upload_title')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>
              {t('photo.upload_note')}
            </div>
            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--color-brand)',
              color: '#fff',
              borderRadius: 'var(--radius-md)',
              padding: '10px 20px',
              fontSize: 13,
              cursor: 'pointer',
            }}>
              <IconUpload size={15} stroke={1.5} />
              {t('photo.choose')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        ) : (
          <>
            {/* Cropper */}
            <div style={{ position: 'relative', height: 320, background: '#000' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom slider */}
            <div style={{ padding: '16px 24px', borderTop: '0.5px solid var(--color-border-light)', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', width: 32, textAlign: 'center' }}>−</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', width: 32, textAlign: 'center' }}>+</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textAlign: 'center', marginTop: 6 }}>
                {t('photo.drag_hint')}
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding: '14px 20px', display: 'flex', gap: 10 }}>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '0.5px solid var(--color-border-medium)',
                borderRadius: 'var(--radius-md)',
                padding: '10px',
                fontSize: 13,
                cursor: 'pointer',
              }}>
                <IconUpload size={14} stroke={1.5} />
                {t('photo.change')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 2,
                  background: 'var(--color-brand)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px',
                  fontSize: 13,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <IconCheck size={15} stroke={1.5} />
                {saving ? t('common.saving') : t('photo.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}